import React, { useState, useEffect } from "react";
import { useStaticQuery, graphql } from "gatsby";

import { normalizeTwoWords, formatImageAlt, findRel } from "../../../shared/utils/stringUtilities";
import useBrowserMode from "../../../shared/hooks/useBrowserMode";
import NavAccordion from "../../atoms/nav-accordion/navAccordion";
import FontSize from "../../atoms/font-size/fontSize";
import Searcher from "../../molecules/searcher/searcher";
import CategoryRecommended from "../../molecules/category-recommended/categoryRecommended";
import RecommenderModal from "../../organisms/recommender-modal/recommenderModal";

import "./navMenu.scss";

const NavMenu = (props: NavMenuInterface) => {
  const { isMenuActive, isSearcherActive, sideLinks } = props;
  const { document } = useBrowserMode();
  const [activeItem, setActiveItem] = useState(0);
  const [isAnyActive, setIsAnyActive] = useState(true);
  const [showFontSize, setShowFontSize] = useState(false);
  const [isRecommenderOpen, setIsRecommenderOpen] = useState(false);

  const data = useStaticQuery(graphql`
    query MENU_CATEGOIAS {
      allContentfulMenuCategorias(
        filter: { titulo: { eq: "Menu bibliotecas" } }
      ) {
        nodes {
          categorias {
            nombreCategoria
            slug
            hyperLink
            referenciaDestacados {
              nombreCategoria
              descripcion {
                raw
              }
              tipoDestacado
              tituloPrincipal
              redireccionBoton
              referenciaDestacados {
                tipoCuerpoDestacado
                tituloGeneral
                referenciaPlantillaContenido {
                  redireccionBoton
                  imagenPrincipal {
                    file {
                      url
                      fileName
                    }
                  }
                  descripcion {
                    raw
                  }
                  tituloBanner
                }
              }
              imagenPrincipal {
                title
                file {
                  url
                  fileName
                }
                gatsbyImageData(
                  layout: CONSTRAINED
                )
              }
            }
            referenciaSubcategorias {
              nombreSubcategoria
              slug
              link
              verEnMenu
              referenciaSubcategorias {
                nombreSubcategoria
                slug
                link
                verEnMenu
              }
            }
          }
        }
      }
      allContentfulFooter(filter: { titulo: { eq: "Footer bibliotecas" } }) {
        nodes {
          categorias {
            nombreCategoria
            referenciaSubcategorias {
              nombreSubcategoria
              link
              titulo
            }
          }
        }
      }
    }
  `);

  const {
    categorias,
    listaDerecha,
  } = data.allContentfulMenuCategorias.nodes[0];
  const footerCategories = data.allContentfulFooter.nodes[0].categorias;

  useEffect(() => {
    const menu = document.querySelector(".m-nav-menu");
    if (menu) menu.scrollTop = 0;
  }, [isMenuActive === true]);

  return (
    <nav
      className={`m-nav-menu ${
        isMenuActive || isSearcherActive ? "" : "m-nav-menu--hide"
      }`}
    >
      {isSearcherActive ? (
        <Searcher 
        type= "global"
        autoFocus= {true}
        />
      ) : (
        <React.Fragment>
          <div className="m-nav-menu__body">
            <div className="m-nav-menu__accordion">
              {categorias &&
                categorias.map((cat, i) => (
                  <NavAccordion
                    key={`cat${i}`}
                    index={i}
                    category={cat}
                    activeItem={activeItem}
                    setActiveItem={setActiveItem}
                    isAnyActive={isAnyActive}
                    setIsAnyActive={setIsAnyActive}
                  />
                ))}
            </div>
            <CategoryRecommended
              isAnyActive={isAnyActive}
              recommended={categorias[activeItem].referenciaDestacados}
            />
          </div>

          <div className="m-nav-menu__sidelinks">
            <ul>
              {sideLinks &&
                sideLinks.map((item, i) => (
                  <li key={`link${i}`}>
                    {item.imagenDestacada[0].description ===
                    "recomendador de libros" ? (
                      <div
                        className={`gtmMenuTrans${normalizeTwoWords(
                          item.nombreCategoria
                        )}`}
                        onClick={() => setIsRecommenderOpen(true)}
                      >
                        <img
                          className={`gtmMenuTrans${normalizeTwoWords(
                            item.nombreCategoria
                          )}`}
                          src={item.imagenDestacada[0].file.url}
                          alt={formatImageAlt(item.imagenDestacada[0].title)}
                        />
                      </div>
                    ) : (
                      <a
                        href={item.hyperLink ? item.hyperLink : "/" + item.slug}
                        target={item.hyperLink && item.nombreCategoria !== "Mis préstamos" ? "_blank" : "_self"}
                        rel={findRel(item.hyperLink)}
                      >
                        <img
                          src={item.imagenDestacada[0].file.url}
                          alt={formatImageAlt(item.imagenDestacada[0].title)}
                        />
                      </a>
                    )}
                  </li>
                ))}
              <li>
                <i
                  className="icon__option icon-font-change-2"
                  onClick={() => setShowFontSize(!showFontSize)}
                />
              </li>
              <li>
                <i
                  className={`icon__option ${
                    props.isDark
                      ? "icon-sun gtmMenuTransTonalidadFondoBlanco"
                      : "icon-moon gtmMenuTransTonalidadFondoNegro"
                  }`}
                  onClick={props.actionTheme}
                />
              </li>
            </ul>
          </div>

          <div className={`float-box ${showFontSize ? "show" : ""}`}>
            <FontSize setShowFontSize={setShowFontSize} />
          </div>

          <div
            className={`m-nav-menu__links ${
              !isAnyActive ? "m-nav-menu__links--locked" : ""
            }`}
          >
            <ul className="m-nav-menu__links-list">
              {listaDerecha &&
                listaDerecha.map((link, i) => (
                  <li
                    key={`cat${i}`}
                    className={`gtmMenuTransLateral${normalizeTwoWords(
                      link.nombreCategoria
                    )}`}
                  >
                    <a
                      className={`gtmMenuTransLateral${normalizeTwoWords(
                        link.nombreCategoria
                      )}`}
                      href={link.hyperLink || "/" + link.slug}
                      target={link.hyperLink ? "_blank" : "_self"} //Temporal. Remove this when new contact center is avaliable
                      rel={findRel(link.hyperLink)}
                    >
                      {" "}
                      {link.nombreCategoria}{" "}
                    </a>
                  </li>
                ))}
            </ul>

            <div className="m-nav-menu__links-social">
              <p>Síguenos en nuestras redes</p>
              <ul>
                {footerCategories[8].nombreCategoria.includes("Síguenos") &&
                  footerCategories[8].referenciaSubcategorias &&
                  footerCategories[8].referenciaSubcategorias.map((item, i) => (
                    <li key={`item${i}`}>
                      <a
                        className={`${
                          item.nombreSubcategoria
                        } gtm${normalizeTwoWords(item.titulo)}`}
                        href={item.link}
                        target="_blank"
                        rel="nofollow noreferrer"
                      ></a>
                    </li>
                  ))}
              </ul>
            </div>

            <p className="m-nav-menu__links-more">
              <span>
                Conoce más en{" "}
                <a href="/" target="_blank" rel="follow">
                  Comfama.com
                </a>
              </span>
            </p>
          </div>

          <RecommenderModal
            open={isRecommenderOpen}
            onClose={() => setIsRecommenderOpen(false)}
          />
        </React.Fragment>
      )}
    </nav>
  );
};

interface NavMenuInterface {
  isMenuActive: boolean;
  isSearcherActive: boolean;
  sideLinks: any;
  isDark: boolean;
  actionTheme: () => void;
}

export default NavMenu;
